<template>
  <LoadingWrapper :loading>
    <ul
      v-if="markets.length"
      class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
    >
      <li
        v-for="market in markets"
        :key="market.key"
        class="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4"
      >
        <div
          class="bg-primary-50 flex size-8 shrink-0 items-center justify-center rounded-lg"
        >
          <BaseIcon :name="market.icon" class="text-primary" />
        </div>
        <div class="flex flex-col gap-1">
          <p class="font-medium">{{ market.label }}</p>
          <p v-if="market.detail" class="text-sm text-gray-500">
            {{ market.detail }}
          </p>
        </div>
      </li>
    </ul>
    <p v-else class="text-gray-500 italic">
      Deze installatie wordt op dit moment op geen enkele markt ingeboden.
    </p>
  </LoadingWrapper>
</template>

<script setup>
import { computed } from "vue"
import { formatCurrency, formatKW } from "@/helpers/formatter.js"
import { BaseIcon, LoadingWrapper } from "@repowerednl/ui-component-library"

const props = defineProps({
  meteringPoint: {
    type: Object,
    default: null,
  },
})

function getCapacityDetail(service) {
  const capacity = service.fcr_capacity ?? service.afrr_capacity
  if (!capacity) {
    return null
  }
  return `Gereserveerd vermogen: ${formatKW(capacity)}`
}

/**
 * The price below which the asset is curtailed instead of sold, in euro per
 * MWh. Also shown as the expected subsidy amount on the SDE panel.
 */
function getStrikePriceDetail(service) {
  if (service.strike_price === null || service.strike_price === undefined) {
    return null
  }
  return `Grensbedrag: ${formatCurrency(service.strike_price)} per MWh`
}

const MARKET_DEFINITIONS = [
  {
    key: "NOMINATION",
    label: "Dagmarkt",
    icon: "calendar",
  },
  {
    key: "INTRADAY_TRADING",
    label: "Intradaymarkt",
    icon: "bar-chart-2",
  },
  {
    key: "IMBALANCE_TRADING",
    label: "Onbalansmarkt",
    icon: "align-vertically",
  },
  {
    key: "IMBALANCE_CURTAILMENT",
    label: "Onbalansmarkt (curtailment)",
    icon: "barricade",
    getDetail: getStrikePriceDetail,
  },
  {
    key: "FCR",
    label: "FCR",
    icon: "time",
    getDetail: getCapacityDetail,
  },
  {
    key: "AFRR",
    label: "aFRR",
    icon: "scales",
    getDetail: getCapacityDetail,
  },
  {
    key: "cbc_curtailment",
    label: "CBC congestiemarkt",
    icon: "network",
  },
]
const flexServices = computed(
  () => props.meteringPoint?.properties?.flex_services,
)

const loading = computed(() => !!props.meteringPoint && !flexServices.value)

const markets = computed(() =>
  MARKET_DEFINITIONS.filter(
    (market) => flexServices.value?.[market.key]?.is_active,
  ).map((market) => ({
    key: market.key,
    label: market.label,
    icon: market.icon,
    detail: market.getDetail?.(flexServices.value[market.key]) ?? null,
  })),
)
</script>
